import React from 'react';

const GDPBoost = () => {
  return (
    <div className="min-h-screen bg-gray-900 py-8 px-4 [&_p]:text-white [&_li]:text-white">
      <div className="max-w-4xl mx-auto">
        {/* Title */}
        <h1 className="text-xl font-bold text-yellow-200 mb-2 text-center sm:text-xl">
          Think5Whys SAKSHAM MSME and SHEETAL Contribution to GDP
        </h1>

        {/* Assumption Base */}
        <section className="mb-2">
          <h2 className="text-xl font-semibold text-gray-100 mb-2 text-center">Assumption Base</h2>
          <div className="grid grid-cols-2 md:grid-cols-2 gap-1 mb-2">
            <div className="bg-gray-800 p-6 rounded-lg shadow-md border border-gray-700">
              <h3 className="text-sm font-bold text-blue-400 mb-2">Total Jobs Created</h3>
              <p className="text-sm text-gray-300">1 crore (10 million) White/Gray Collar Job</p>
            </div>
            <div className="bg-gray-800 p-6 rounded-lg shadow-md border border-gray-700">
              <h3 className="text-sm font-bold text-blue-400 mb-2">Implementation Period</h3>
              <p className="text-sm text-gray-300">5 Years</p>
            </div>
            <div className="bg-gray-800 p-6 rounded-lg shadow-md border border-gray-700">
              <h3 className="text-sm font-bold text-blue-400 mb-2">Average Salary per Year</h3>
              <p className="text-sm text-gray-300">₹4 lakh</p>
            </div>
            <div className="bg-gray-800 p-6 rounded-lg shadow-md border border-gray-700">
              <h3 className="text-sm font-bold text-blue-400 mb-2">Total Salary Outlay</h3>
              <p className="text-sm text-gray-300">₹4 lakh crore/year</p>
            </div>
          </div>
        </section>

        {/* 1. Wages and Salaries */}
        <section className="mb-2">
          <h2 className="text-xl font-semibold text-yellow-200 mb-2">1. Wages and Salaries = Direct GDP Contribution (Income Method)</h2>
          <p className="text-gray-300 mb-2">
            <strong>Explanation:</strong> The Income Method of calculating GDP: GDP = Wages + Rent + Interest + Profit. So the ₹4 lakh crore paid as wages/salaries will directly enter GDP computation.
          </p>
          <p className="text-gray-300 mb-2">
            <strong>Calculation:</strong> Total Wage Outlay = 1 crore employees × ₹4 lakh salary/year = ₹4,00,000 × 1,00,00,000 = ₹4,00,000 crore = ₹4 lakh crore
          </p>
          <p className="text-gray-300 mb-2">
            <strong>Conclusion:</strong> This ₹4 lakh crore is a direct addition to GDP through the income method, assuming it is formally recorded.
          </p>
        </section>

        {/* 2. Multiplier Effect */}
        <section className="mb-2">
          <h2 className="text-xl font-semibold text-yellow-200  mb-2">2. Multiplier Effect = Indirect GDP Contribution (Consumption Method)</h2>
          <p className="text-gray-300 mb-2">
            <strong>Explanation:</strong> When people earn salaries, they spend a portion of it on food, clothing, housing, transport etc. This increases demand, prompting more production and hence more GDP.
          </p>
          <p className="text-gray-300 mb-2">
            <strong>Calculation:</strong> Assume Marginal Propensity to Consume (MPC) = 0.75 (i.e., 75% of income is spent). Then, spending out of ₹4 lakh crore = ₹4 lakh crore × 0.75 = ₹3 lakh crore. Applying a Keynesian Multiplier: Multiplier=1/(1−MPC)=1/(1−0.75)=4. Secondary GDP Generated: Additional GDP=₹3 lakh crore×4=₹12 lakh crore
          </p>
          <p className="text-gray-300 mb-2">
            <strong>Conclusion:</strong> ₹4 lakh crore in salaries may indirectly generate ₹12 lakh crore more GDP via consumption and production cycles.
          </p>
        </section>

        {/* 3. Employment Generation */}
        <section className="mb-2">
          <h2 className="text-xl font-semibold text-yellow-200  mb-2">3. Employment Generation = Increased Productivity and Formal Economy Size</h2>
          <p className="text-gray-300 mb-2">
            <strong>Explanation:</strong> 1 crore jobs mean 10 million people now contributing productively. This: Reduces dependency ratio, Increases tax base, Boosts industrial/service sector outputs, Creates network effects (e.g., support services, logistics, housing, insurance)
          </p>
          <p className="text-gray-300 mb-2">
            <strong>Illustrative Output Contribution:</strong> Assuming each job contributes ₹6 lakh in output value per annum (through goods or services produced). Then total output = ₹6 lakh × 1 crore = ₹6 lakh crore
          </p>
          <p className="text-gray-300 mb-2">
            <strong>Conclusion:</strong> The economic output of newly employed workers boosts GDP by ₹6 lakh crore, aside from salary impact.
          </p>
        </section>

        {/* 4. Formalization */}
        <section className="mb-2">
          <h2 className="text-xl font-semibold text-yellow-200 mb-2">4. Formalization = Inclusion in Official GDP Stats</h2>
          <p className="text-gray-300 mb-2">
            <strong>Explanation:</strong> For this entire ₹4 lakh crore to show up in official GDP, MSMEs and jobs must be: Udyam registered, GST and Income Tax compliant, Bank-channelled salary payments, EPFO/ESIC-linked employment
          </p>
          <p className="text-gray-300 mb-2">
            <strong>Estimation:</strong> If only 60% of MSMEs and jobs are formalized, then: Formal salary recorded = ₹4 lakh crore × 0.6 = ₹2.4 lakh crore. Rest may remain undercounted or in the informal economy
          </p>
          <p className="text-gray-300 mb-2">
            <strong>Conclusion:</strong> Policy support to formalize MSMEs can unlock full GDP recording, maximizing national benefits and strategic visibility.
          </p>
        </section>

        {/* Caveats & Considerations */}
        <section className="mb-2">
          <h2 className="text-xl font-semibold text-yellow-200  mb-2 text-left">Caveats & Considerations</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-gray-800 border border-gray-600 rounded-lg shadow-md">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">#</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Factor</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Caveat</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Implication</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-600">
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-100">1</td>
                  <td className="px-4 py-2 text-sm text-gray-100">Informality</td>
                  <td className="px-4 py-2 text-sm text-gray-100">Informal wages not bank-recorded may not be captured in GDP</td>
                  <td className="px-4 py-2 text-sm text-gray-100">GDP may be understated</td>
                </tr>
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-100">2</td>
                  <td className="px-4 py-2 text-sm text-gray-100">Leakages</td>
                  <td className="px-4 py-2 text-sm text-gray-100">Consumption might go into imports or black economy</td>
                  <td className="px-4 py-2 text-sm text-gray-100">Multiplier effect reduces</td>
                </tr>
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-100">3</td>
                  <td className="px-4 py-2 text-sm text-gray-100">Skill Mismatch</td>
                  <td className="px-4 py-2 text-sm text-gray-100">Low-skilled jobs may yield low output per worker</td>
                  <td className="px-4 py-2 text-sm text-gray-100">Output-side GDP gains less impactful</td>
                </tr>
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-100">4</td>
                  <td className="px-4 py-2 text-sm text-gray-100">Underemployment</td>
                  <td className="px-4 py-2 text-sm text-gray-100">Workers may be part-time or seasonal</td>
                  <td className="px-4 py-2 text-sm text-gray-100">Reduces full GDP potential</td>
                </tr>
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-100">5</td>
                  <td className="px-4 py-2 text-sm text-gray-100">Inflation</td>
                  <td className="px-4 py-2 text-sm text-gray-100">₹4 lakh crore injection can cause inflation</td>
                  <td className="px-4 py-2 text-sm text-gray-100">Real GDP growth might be less than nominal</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Summary */}
        <section className="mb-2">
          <h2 className="text-xl font-semibold text-yellow-200 mb-2 text-left">Summary</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-gray-800 border border-gray-600 rounded-lg shadow-md">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Impact Type</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Estimate (₹ Cr)</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Method</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-600">
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">Direct Salary GDP</td>
                  <td className="px-4 py-2 text-sm text-gray-100">4,00,000</td>
                  <td className="px-4 py-2 text-sm text-gray-100">Income Method</td>
                </tr>
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">Indirect Consumption GDP</td>
                  <td className="px-4 py-2 text-sm text-gray-100">12,00,000</td>
                  <td className="px-4 py-2 text-sm text-gray-100">Keynesian Multiplier</td>
                </tr>
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">Productivity Output</td>
                  <td className="px-4 py-2 text-sm text-gray-100">6,00,000</td>
                  <td className="px-4 py-2 text-sm text-gray-100">Output Estimation</td>
                </tr>
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">Total Potential GDP</td>
                  <td className="px-4 py-2 text-sm text-gray-100">22,00,000</td>
                  <td className="px-4 py-2 text-sm text-gray-100">Cumulative</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-gray-300 mt-4 text-center">
            Additionally: GST & Income Tax Contribution of approx 1 Lakh Crore Rs (Given in Annexure 1)
          </p>
          <p className="text-gray-300 mt-4 text-center">
            Note: This is an idealized scenario. Real contribution would depend on formalization level, execution quality and economic conditions.
          </p>
        </section>

        {/* India’s Current GDP */}
        <section className="mb-2">
          <h2 className="text-xl font-semibold text-yellow-200  mb-2 text-center">India’s Current GDP (2024–25)</h2>
          <p className="text-gray-300 mb-2">
            As per official estimates (budget and RBI projections): India’s Nominal GDP (2024–25): ≈ ₹327 lakh crore (i.e., ₹327 trillion or ₹327,00,000 crore)
          </p>
          <p className="text-gray-300 mb-2">Source: Union Budget 2024–25, Ministry of Finance</p>
        </section>

        {/* Percentage Calculation */}
        <section className="mb-2">
          <h2 className="text-xl font-semibold text-yellow-200  mb-2 text-center">Percentage Calculation</h2>
          <p className="text-gray-300 mb-2">
            Percent of GDP=(₹4 lakh crore / ₹327 lakh crore) × 100  
          </p>
          <p className="text-gray-300 mb-2">
            ₹4 lakh crore salary contribution accounts for approximately 1.22% of India’s GDP in 5 years ie 0.44% yearly in 5 years 
          </p>
          <p className="text-gray-300 mb-2">
            The multiplier effects and productive output discussed earlier are realized, Think5Whys initiative could influence up to 6–7% of GDP indirectly in 5 years ie 1.2 to 1.4% yearly.
          </p>
        </section>

        {/* Per Capita Income Increase */}
        <section className="mb-2">
          <h2 className="text-xl font-semibold text-yellow-200  mb-2 text-center">Think5Whys SAKSHAM MSME & SHEETAL initiative will increase India's per capita income.</h2>
          <h3 className="text-lg font-bold text-blue-400 mb-2">Step-by-Step Calculation: Will It Increase Per Capita Income?</h3>
          <h4 className="text-base font-semibold text-gray-100 mb-2">Step 1: Base Data (India FY 2024–25 Estimates)</h4>
          <div className="overflow-x-auto mb-2">
            <table className="min-w-full bg-gray-800 border border-gray-600 rounded-lg shadow-md">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Metric</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Value</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-600">
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">India's Population (2025)</td>
                  <td className="px-4 py-2 text-sm text-gray-100">~1.43 billion (143 crore)</td>
                </tr>
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">GDP (Nominal)</td>
                  <td className="px-4 py-2 text-sm text-gray-100">₹327 lakh crore</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-gray-300 mb-2">Per Capita Income (PCI) = GDP ÷ Population → ₹327 lakh crore ÷ 143 crore = ₹2.29 lakh/year ≈ ₹2.3 lakh per capita</p>
          <h4 className="text-base font-semibold text-gray-100 mb-2">Step 2: Adding ₹4 Lakh Crore in Salary Contribution</h4>
          <p className="text-gray-300 mb-2">If ₹4 lakh crore is injected via formal salary payments, India's GDP becomes: New GDP=₹327+₹4=₹331 lakh crore PCI=₹331 lakh crore÷143 crore≈₹2.31 lakh</p>
          <p className="text-gray-300 mb-2">PCI Increase: ₹2.31 lakh – ₹2.30 lakh = ~₹1,000 per person</p>
          <p className="text-gray-300 mb-2">But that’s only from direct salary injection.</p>
          <h4 className="text-base font-semibold text-gray-100 mb-2">Step 3: Adding Total Impact (Multiplier + Output)</h4>
          <p className="text-gray-300 mb-2">Assuming total GDP boost is ₹22 lakh crore (as discussed earlier), New GDP becomes: 327+22=₹349 lakh crore</p>
          <p className="text-gray-300 mb-2">New PCI= ₹349 lakh crore ÷143 crore=₹2.44 lakh</p>
          <p className="text-gray-300 mb-2">PCI Increase: ₹2.44 lakh – ₹2.30 lakh = ~₹14,000 per person/year</p>
          <div className="overflow-x-auto mb-2">
            <table className="min-w-full bg-gray-800 border border-gray-600 rounded-lg shadow-md">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Scenario</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">GDP Increase</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">PCI Increase</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-600">
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">Direct Salary Only</td>
                  <td className="px-4 py-2 text-sm text-gray-100">₹4 lakh Cr</td>
                  <td className="px-4 py-2 text-sm text-gray-100">₹1,000 per person</td>
                </tr>
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">Full Impact Realized (₹22 lakh Cr)</td>
                  <td className="px-4 py-2 text-sm text-gray-100">₹22 lakh Cr</td>
                  <td className="px-4 py-2 text-sm text-gray-100">₹14,000 per person</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Key Points */}
        <section className="mb-2">
          <h2 className="text-xl font-semibold text-yellow-200  mb-2 text-center">Key Points</h2>
          <ul className="space-y-3 text-gray-300">
            <li>• It increases PCI — directly by injecting income and indirectly by increasing employment and productivity.</li>
            <li>• The more formalized and inclusive Think5Whys initiative is, the larger the per capita impact.</li>
            <li>• Most of the benefit will go to lower-income groups, which is excellent for reducing inequality and improving quality of life.</li>
          </ul>
        </section>

        {/* Strategic Roadmap */}
        <section className="mb-2">
          <h2 className="text-xl font-semibold text-yellow-200  mb-2 text-center">Strategic and multi-source roadmap for infusing ₹4 lakh crore/year salary into the MSME ecosystem without overwhelming the government’s fiscal capacity.</h2>
          <p className="text-gray-300 mb-2">
            <strong>Key Principle:</strong> The ₹4 lakh crore can be mobilized through a mix of public, private, institutional and innovative financing models - not just government subsidies.
          </p>
          <ol className="space-y-4 text-gray-300 mb-2">
            <li className="flex items-start">
              <span className="font-medium text-blue-400 mr-2 mt-0.5">1.</span>
              <div>
                <strong className="text-gray-100">Public–Private Partnership (PPP) Model</strong>
                <p className="mt-1">Involve corporates, financial institutions and industry bodies in co-funding job creation under government frameworks.</p>
                <p className="text-gray-300 mb-2"><strong>Allocation:</strong> Government covers 30–40% (₹1.2 – 1.6 lakh crore), Private sector co-invests 60–70% via CSR, hiring incentives or industry tie-ups</p>
                <p className="text-gray-300"><strong>Benefit:</strong> Reduces GoI burden to ₹1.5 lakh crore or less, while driving market-led employment.</p>
              </div>
            </li>
            <li className="flex items-start">
              <span className="font-medium text-blue-400 mr-2 mt-0.5">2.</span>
              <div>
                <strong className="text-gray-100">Skill-Linked Salary Subsidy Schemes (like PMKVY 2.0+)</strong>
                <p className="mt-1">The government provides wage-linked incentives for jobs given to trained/skilled workers from accredited programs.</p>
                <p className="text-gray-300 mb-2"><strong>Mechanism:</strong> For first-year employment, Govt pays ₹1–2 lakh per worker, Employer pays the rest (₹2–3 lakh), 1 crore workers → ₹1.5 lakh avg subsidy → ₹1.5 lakh crore</p>
                <p className="text-gray-300"><strong>Benefit:</strong> Targets new job creation; reduces waste; money goes directly to MSMEs for real hiring.</p>
              </div>
            </li>
            <li className="flex items-start">
              <span className="font-medium text-blue-400 mr-2 mt-0.5">3.</span>
              <div>
                <strong className="text-gray-100">MSME Employment Bonds / Job-Linked Capital Instruments</strong>
                <p className="mt-1">Launch sovereign-backed employment bonds or development impact bonds (DIBs).</p>
                <p className="text-gray-300 mb-2"><strong>Structure:</strong> Investors fund employment programs, Govt or third-party repays only if jobs are created and sustained</p>
                <p className="text-gray-300"><strong>Benefit:</strong> Leverages impact investing, CSR and sovereign ratings without upfront spending.</p>
              </div>
            </li>
            <li className="flex items-start">
              <span className="font-medium text-blue-400 mr-2 mt-0.5">4.</span>
              <div>
                <strong className="text-gray-100">Credit-Linked MSME Payroll Schemes (via SIDBI, Banks)</strong>
                <p className="mt-1">Facilitate working capital loans to MSMEs specifically for paying salaries, with partial interest subvention from the govt.</p>
                <p className="text-gray-300 mb-2"><strong>Structure:</strong> MSMEs borrow at low rates (4–6%), Govt guarantees or subsidizes interest (not principal), Leverage ₹40,000 Cr budget to generate ₹4 lakh Cr salary payments</p>
                <p className="text-gray-300"><strong>Benefit:</strong> Leverages the financial system for payroll liquidity. Reduces risk for MSMEs.</p>
              </div>
            </li>
            <li className="flex items-start">
              <span className="font-medium text-blue-400 mr-2 mt-0.5">5.</span>
              <div>
                <strong className="text-gray-100">Startup India, Mudra and Udyam-Linked Incentives</strong>
                <p className="mt-1">Use existing schemes (Startup India, Mudra loans, Udyam portal) to link job-based performance bonuses.</p>
                <p className="text-gray-300 mb-2"><strong>Use Case:</strong> Startups/MSMEs get ₹50,000–1 lakh/employee for verified jobs, Disbursed via DBT linked to PF/ESIC registration</p>
                <p className="text-gray-300"><strong>Benefit:</strong> Aligns with already budgeted programs, without new schemes.</p>
              </div>
            </li>
            <li className="flex items-start">
              <span className="font-medium text-blue-400 mr-2 mt-0.5">6.</span>
              <div>
                <strong className="text-gray-100">Phased Rollout (3-Year Ramp-Up Plan)</strong>
                <p className="mt-1">Spread the ₹4 lakh crore over 3 years, creating 33–35 lakh jobs per year.</p>
                <p className="text-gray-300 mb-2"><strong>Timeline:</strong> Year 1: ₹1.25 lakh crore, Year 2: ₹1.35 lakh crore, Year 3: ₹1.40 lakh crore</p>
                <p className="text-gray-300"><strong>Benefit:</strong> Makes budgeting fiscally sustainable, allows tracking and course correction.</p>
              </div>
            </li>
          </ol>
          <div className="overflow-x-auto mb-2">
            <table className="min-w-full bg-gray-800 border border-gray-600 rounded-lg shadow-md">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Source</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Contribution (Est.)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-600">
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">Govt. Wage Subsidy (Skill-linked, DBT)</td>
                  <td className="px-4 py-2 text-sm text-gray-100">₹1.5 lakh crore</td>
                </tr>
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">Private Sector & PPP</td>
                  <td className="px-4 py-2 text-sm text-gray-100">₹1.2 lakh crore</td>
                </tr>
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">MSME Payroll Financing via SIDBI/Banks</td>
                  <td className="px-4 py-2 text-sm text-gray-100">₹1.0 lakh crore</td>
                </tr>
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">CSR/Philanthropy/Impact Bonds</td>
                  <td className="px-4 py-2 text-sm text-gray-100">₹0.3 lakh crore</td>
                </tr>
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">Total</td>
                  <td className="px-4 py-2 text-sm text-gray-100">₹4.0 lakh crore</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-gray-300 mb-2">
            <strong>Why This Model Works:</strong> No full fiscal burden on the government, Creates real jobs, not just handouts, Ensures accountability via formal payrolls, Involves banks, private sector and investors, Can be phased and targeted regionally or sector-wise
          </p>
          <p className="text-gray-300 mb-2">
            Sanjeev Kumar, Founder & Chief Architect has created Think5Whys in all adversity, a nation-scale employment and GDP-impacting model under brand Think5Whys Global DeepTech Solutions and naturally Think5Whys should: Retain ownership & IP, Ensure fair recognition and financial returns, Scale nationally without being sidelined, Balance social impact with sustainability
          </p>
        </section>

        {/* SPV Model */}
        <section className="mb-2">
          <h2 className="text-xl font-semibold text-yellow-200  mb-2 text-center">So,the Best Structure: An SPV (Special Purpose Vehicle) is one of the most strategic solutions — it allows the innovator to scale, collaborate with government and private partners and still retain control, ownership and long-term benefits.</h2>
          <p className="text-gray-300 mb-2">Let’s justify this in detail:</p>

          {/* Why Not Just “Give” It to the Government? */}
          <h3 className="text-lg font-bold text-blue-400 mb-2">1. Why Not Just “Give” It to the Government?</h3>
          <p className="text-gray-300 mb-2">Simply handing it over would: Risk losing control/IP, Risk brand being diluted, Making implementation fragmented or misaligned with vision, Offer no financial sustainability for founder or Think5Whys and it's team</p>

          {/* The SPV Model */}
          <h3 className="text-lg font-bold text-blue-400 mb-2">2. The SPV Model</h3>
          <p className="text-gray-300 mb-2">A Special Purpose Vehicle (SPV), a separate legal entity, typically a private limited company or Section 8 company (for impact), should be created to implement a specific project or initiative. It allows: Partnership with Govt, corporates, banks, investors, Contracting, funding, execution at scale, Clear ownership structure, Risk isolation and IP protection</p>

          {/* How SPV Can Work for Think5Whys */}
          <h3 className="text-lg font-bold text-blue-400 mb-2">3. How SPV Can Work for Think5Whys</h3>
          <div className="overflow-x-auto mb-2">
            <table className="min-w-full bg-gray-800 border border-gray-600 rounded-lg shadow-md">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Element</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">How Benefit via SPV</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-600">
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">Ownership</td>
                  <td className="px-4 py-2 text-sm text-gray-100">Think5Whys can retain majority or founding stake in SPV</td>
                </tr>
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">IP Protection</td>
                  <td className="px-4 py-2 text-sm text-gray-100">Methodology, tech platform, content, tools are licensed to SPV</td>
                </tr>
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">Govt/Corporate Participation</td>
                  <td className="px-4 py-2 text-sm text-gray-100">Govt/CSR/Donors invest in SPV without owning it</td>
                </tr>
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">Revenue Model</td>
                  <td className="px-4 py-2 text-sm text-gray-100">SPV earns via platform usage, program management fees, training, success-based incentives</td>
                </tr>
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">Control</td>
                  <td className="px-4 py-2 text-sm text-gray-100">Think5Whys define operational guidelines, execution models</td>
                </tr>
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">Brand Visibility</td>
                  <td className="px-4 py-2 text-sm text-gray-100">Think5Whys becomes the official Knowledge + Tech Partner</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Example SPV Structure */}
          <h3 className="text-lg font-bold text-blue-400 mb-2">4. Example SPV Structure</h3>
          <div className="overflow-x-auto mb-2">
            <table className="min-w-full bg-gray-800 border border-gray-600 rounded-lg shadow-md">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Stakeholder</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Role</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">% Stake (Suggested)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-600">
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">Think5Whys</td>
                  <td className="px-4 py-2 text-sm text-gray-100">IP, Platform, Strategy</td>
                  <td className="px-4 py-2 text-sm text-gray-100">51%</td>
                </tr>
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">Govt (State/Central)</td>
                  <td className="px-4 py-2 text-sm text-gray-100">Policy, Support, Funding</td>
                  <td className="px-4 py-2 text-sm text-gray-100">20%</td>
                </tr>
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">Corporate/CSR Investor</td>
                  <td className="px-4 py-2 text-sm text-gray-100">Funds under ESG/CSR</td>
                  <td className="px-4 py-2 text-sm text-gray-100">20%</td>
                </tr>
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">Social Impact Org/Trust</td>
                  <td className="px-4 py-2 text-sm text-gray-100">Ground Outreach</td>
                  <td className="px-4 py-2 text-sm text-gray-100">9%</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Other Mechanisms */}
          <h3 className="text-lg font-bold text-blue-400 mb-2">5. Other Mechanisms to Protect & Monetize Founder Role</h3>
          <div className="overflow-x-auto mb-2">
            <table className="min-w-full bg-gray-800 border border-gray-600 rounded-lg shadow-md">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Mechanism</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Purpose</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-600">
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">IP License Agreement</td>
                  <td className="px-4 py-2 text-sm text-gray-100">SPV licenses Think5Whys tools, frameworks annually</td>
                </tr>
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">Platform-as-a-Service</td>
                  <td className="px-4 py-2 text-sm text-gray-100">Think5Whys provides the tech as a managed service</td>
                </tr>
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">Revenue Share</td>
                  <td className="px-4 py-2 text-sm text-gray-100">Think5Whys earns % on each job/salary milestone achieved</td>
                </tr>
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">Performance Fees</td>
                  <td className="px-4 py-2 text-sm text-gray-100">Based on job creation or salary disbursed KPIs</td>
                </tr>
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">Equity Holding</td>
                  <td className="px-4 py-2 text-sm text-gray-100">Think5Whys holds equity for long-term upside (valuation, exit)</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Think5Whys Narrative */}
          <h3 className="text-lg font-bold text-blue-400 mb-2">6. Think5Whys Narrative: Cause + Capability</h3>
          <p className="text-gray-300 mb-2">The founder doesn't need to pretend to be just a tech enabler. The Founder is the originator, ecosystem architect and transformation catalyst. Founder's unique value is the founder have: The vision, The execution frameworks, The tech+thinking IP, Skin in the game for long-term success</p>
          <p className="text-gray-300 mb-2">So the "big cause" doesn’t eliminate founder capability - it amplifies founder’s role.</p>

          {/* What Next */}
          <h3 className="text-lg font-bold text-blue-400 mb-2">What Next</h3>
          <ul className="space-y-2 text-gray-300 mb-2">
            <li>• Incorporating an SPV – e.g., “Think5Whys Employment Mission SPV Pvt Ltd”</li>
            <li>• Draft MoUs and IP Licensing – between SPV and parent entity</li>
            <li>• Preparing a Strategic Partner Pitch Deck – targeting: Ministries (MSME, Skill, Labour, Commerce), CSR wings of top corporates (Tata, Reliance, Adani etc.), Multilateral bodies (World Bank, UNDP etc.)</li>
            <li>• Creating a Governance and Revenue Model – showing how impact and sustainability co-exist</li>
          </ul>

          {/* Summary SPV */}
          <h3 className="text-lg font-bold text-blue-400 mb-2">Summary</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-gray-800 border border-gray-600 rounded-lg shadow-md">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Goal</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Solution</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-600">
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">Protect ownership & IP</td>
                  <td className="px-4 py-2 text-sm text-gray-100">Create SPV with Think5Whys as founding stakeholder</td>
                </tr>
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">Enable Govt/Private partnerships</td>
                  <td className="px-4 py-2 text-sm text-gray-100">Bring them into SPV as investors/supporters</td>
                </tr>
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">Scale nationally</td>
                  <td className="px-4 py-2 text-sm text-gray-100">Use SPV to execute contracts, raise funds, manage operations</td>
                </tr>
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">Monetize and sustain</td>
                  <td className="px-4 py-2 text-sm text-gray-100">License IP, earn platform fees and equity value</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Annexure 1 */}
        <section className="mb-2">
          <h2 className="text-xl font-semibold text-gray-100 mb-2 text-center">Annexure 1: Extended Fiscal Contribution Analysis of ₹4 Lakh Crore Salary-Based MSME Model</h2>

          {/* GST Revenue from Consumption */}
          <h3 className="text-lg font-bold text-blue-400 mb-2">1. GST Revenue from Consumption</h3>
          <p className="text-gray-300 mb-2"><strong>Assumption:</strong> ₹4 lakh crore paid as salary, 75% of it is spent (MPC = 0.75) → ₹3 lakh crore spent, Assume average GST rate of 12% across goods/services consumed (a conservative mix of 5%, 12%, 18%, 28%)</p>
          <p className="text-gray-300 mb-2"><strong>Calculation:</strong> GST Revenue= ₹3 lakh crore×12%= ₹36,000 crore</p>
          <p className="text-gray-300 mb-2">Result: ₹36,000 crore added to government revenues via GST, annually.</p>

          {/* Indirect Tax Uplift */}
          <h3 className="text-lg font-bold text-blue-400 mb-2">2. Indirect Tax Uplift from Increased Output</h3>
          <p className="text-gray-300 mb-2">From the multiplier effect, total output → ₹12 lakh crore, Assuming only 60% of this is taxed output (formal sector, goods/services covered under GST)</p>
          <p className="text-gray-300 mb-2">Taxable output= ₹12 lakh crore× 60%= ₹7.2 lakh crore </p>
          <p className="text-gray-300 mb-2">GST on this= ₹7.2 lakh crore× 12%= ₹86,400 crore</p>
          <p className="text-gray-300 mb-2">Result: Additional ₹86,400 crore GST from indirect output multiplier.</p>

          {/* Income Tax Considerations */}
          <h3 className="text-lg font-bold text-blue-400 mb-2">3. Income Tax Considerations</h3>
          <p className="text-gray-300 mb-2">Now to salaries. Assumption: ₹4 lakh crore paid to 1 crore employees, Avg salary = ₹4 lakh/year, Current Income Tax threshold = ₹12 lakh/year (with rebate)</p>
          <p className="text-gray-300 mb-2">So, the majority may not pay tax directly, but some will. Let’s assume: 1% earn > ₹12 lakh/year</p>
          <p className="text-gray-300 mb-2">Effective taxable population: 1 lakh individuals, Assume average tax per individual: ₹25,000</p>
          <p className="text-gray-300 mb-2">Income Tax= 1 lakh × ₹25,000= ₹250 crore</p>
          <p className="text-gray-300 mb-2">Result: ₹250 crore/year in new Income Tax collections.</p>

          {/* Total Fiscal */}
          <h3 className="text-lg font-bold text-blue-400 mb-2">Total Fiscal (Tax) Contribution Summary</h3>
          <div className="overflow-x-auto mb-2">
            <table className="min-w-full bg-gray-800 border border-gray-600 rounded-lg shadow-md">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Component</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Estimate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-600">
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">GST from Salary Spending</td>
                  <td className="px-4 py-2 text-sm text-gray-100">₹36,000 crore</td>
                </tr>
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">GST from Multiplier Output</td>
                  <td className="px-4 py-2 text-sm text-gray-100">₹86,400 crore</td>
                </tr>
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">Income Tax on Salaries</td>
                  <td className="px-4 py-2 text-sm text-gray-100">₹250 crore</td>
                </tr>
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">Total Annual Tax Contribution</td>
                  <td className="px-4 py-2 text-sm text-gray-100">~₹1.23 lakh crore</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-gray-300 mb-2">
            <strong>Final Summary:</strong> ₹4 lakh crore MSME salary initiative could generate ₹1.20 lakh crore/year in new tax revenues for the government - even without putting the entire cost burden on them. So, “We’re not asking for a ₹4 lakh crore subsidy. We’re proposing an investment that can generate over ₹22 lakh crore in GDP impact and return ₹1.20 lakh crore/year in taxes back to the government. It’s a sustainable, self-financing national transformation model directed towards process & quality centric India using its vast workforce of dedicated youth.”
          </p>
        </section>

        {/* Annexure 2 */}
        <section className="mb-2">
          <h2 className="text-xl font-semibold text-gray-100 mb-2 text-center">Annexure 2: Justification of 12% Average GST Rate assumption</h2>
          <p className="text-gray-300 mb-2">It’s Reasonable and Conservative for a Mixed Consumption Basket.</p>
          <h3 className="text-lg font-bold text-blue-400 mb-2">Typical Consumption Basket for Blue-Collar/Lower-Middle Class Workers (who’ll receive these salaries)</h3>
          <div className="overflow-x-auto mb-2">
            <table className="min-w-full bg-gray-800 border border-gray-600 rounded-lg shadow-md">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Expense Type</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Share of Spending</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">GST Rate</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Effective GST Weight</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-600">
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">Food & essentials (e.g., atta, rice, pulses)</td>
                  <td className="px-4 py-2 text-sm text-gray-100">25%</td>
                  <td className="px-4 py-2 text-sm text-gray-100">0–5%</td>
                  <td className="px-4 py-2 text-sm text-gray-100">1%</td>
                </tr>
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">Clothing & footwear</td>
                  <td className="px-4 py-2 text-sm text-gray-100">10%</td>
                  <td className="px-4 py-2 text-sm text-gray-100">5–12%</td>
                  <td className="px-4 py-2 text-sm text-gray-100">1%</td>
                </tr>
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">Mobile/internet/electronics</td>
                  <td className="px-4 py-2 text-sm text-gray-100">10%</td>
                  <td className="px-4 py-2 text-sm text-gray-100">18%</td>
                  <td className="px-4 py-2 text-sm text-gray-100">1.8%</td>
                </tr>
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">Transportation (public/private fuel)</td>
                  <td className="px-4 py-2 text-sm text-gray-100">10%</td>
                  <td className="px-4 py-2 text-sm text-gray-100">Non-GST (fuel)/5–12%</td>
                  <td className="px-4 py-2 text-sm text-gray-100">0.5%</td>
                </tr>
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">Rent/housing</td>
                  <td className="px-4 py-2 text-sm text-gray-100">20%</td>
                  <td className="px-4 py-2 text-sm text-gray-100">Mostly GST-exempt</td>
                  <td className="px-4 py-2 text-sm text-gray-100">0%</td>
                </tr>
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">Consumer goods (TV, fridge, AC, mixer)</td>
                  <td className="px-4 py-2 text-sm text-gray-100">10%</td>
                  <td className="px-4 py-2 text-sm text-gray-100">18–28%</td>
                  <td className="px-4 py-2 text-sm text-gray-100">2.5%</td>
                </tr>
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">Services (movies, DTH etc.)</td>
                  <td className="px-4 py-2 text-sm text-gray-100">5%</td>
                  <td className="px-4 py-2 text-sm text-gray-100">18%</td>
                  <td className="px-4 py-2 text-sm text-gray-100">0.9%</td>
                </tr>
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">Healthcare, education</td>
                  <td className="px-4 py-2 text-sm text-gray-100">10%</td>
                  <td className="px-4 py-2 text-sm text-gray-100">Exempt or 5%</td>
                  <td className="px-4 py-2 text-sm text-gray-100">0.2%</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-gray-300 mb-2">Weighted Average GST Rate = Sum of Effective GST Weights: 1+1+1.8+0.5+0+2.5+0.9+0.2=7.9</p>
          <p className="text-gray-300 mb-2">Conservative Avg GST Rate: 7.5–8%</p>
          <p className="text-gray-300 mb-2">Most beneficiaries are lower-middle income, 8% is more realistic. If middle-income consumption picks up (e.g., with rising salaries or urban demand), the average GST rate can go up to 10–12%.</p>
          <p className="text-gray-300 mb-2">Using 8% avg GST rate on ₹3 lakh crore spending: ₹3,00,000crore×8%=₹24,000crore Still significant.</p>
          <h3 className="text-lg font-bold text-blue-400 mb-2">Strategic Takeaway:</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-gray-800 border border-gray-600 rounded-lg shadow-md">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Scenario</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Avg GST Rate</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Revenue Estimate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-600">
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">Conservative</td>
                  <td className="px-4 py-2 text-sm text-gray-100">8%</td>
                  <td className="px-4 py-2 text-sm text-gray-100">₹24,000 crore</td>
                </tr>
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">Moderate</td>
                  <td className="px-4 py-2 text-sm text-gray-100">10%</td>
                  <td className="px-4 py-2 text-sm text-gray-100">₹30,000 crore</td>
                </tr>
                <tr className="hover:bg-gray-700">
                  <td className="px-4 py-2 text-sm text-gray-100">Ambitious/Urban-centric</td>
                  <td className="px-4 py-2 text-sm text-gray-100">12%</td>
                  <td className="px-4 py-2 text-sm text-gray-100">₹36,000 crore</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-gray-300 mt-4">
            12% is on the higher side, but not wildly ambitious — it would require: Urban or middle-income consumption, Higher share of taxed discretionary spending, A mix of rural–urban jobs under the initiative, For safe budgeting using 8–10% GST range.
          </p>
        </section>
      </div>
    </div>
  );
};

export default GDPBoost;